import { useEffect, useState } from "react";
import "./admin.css";
import {
  fetchSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
} from "../utils/api";

const emptyForm = {
  title: "",
  description: "",
  price: "",
  price_ils: "",
  duration_days: 30,
  no_credits: "",
  total_free_prints: "",
  per_print_limit: "",
  total_free_call: "",
  time_limit_per_call: 60,
};

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchSubscriptions();
      setSubscriptions(data || []);
    } catch (err) {
      alert(err.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(false);
  };

  const edit = (sub) => {
    setEditingId(sub.id);
    setForm({
      title: sub.title || "",
      description: sub.description || "",
      price: sub.price || "",
      price_ils: sub.price_ils || "",
      duration_days: sub.duration_days || 30,
      no_credits: sub.no_credits || "",
      total_free_prints: sub.total_free_prints || "",
      per_print_limit: sub.per_print_limit || "",
      total_free_call: sub.total_free_call || "",
      time_limit_per_call: sub.time_limit_per_call || 60,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        title: form.title,
        description: form.description,
        price: Number(form.price),
        price_ils: Number(form.price_ils),
        duration_days: Number(form.duration_days),
        credits: null,
        no_credits: Number(form.no_credits),
        total_free_prints: Number(form.total_free_prints),
        per_print_limit: Number(form.per_print_limit),
        total_free_call: Number(form.total_free_call),
        time_limit_per_call: Number(form.time_limit_per_call),
      };

      if (editingId) {
        await updateSubscription(editingId, payload);
      } else {
        await createSubscription(payload);
      }

      closeModal();
      await load();
    } catch (err) {
      alert(err.message || "Failed to save subscription");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this subscription plan?")) return;

    try {
      await deleteSubscription(id);
      await load();
    } catch (err) {
      alert(err.message || "Failed to delete subscription");
    }
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Subscriptions</h1>
          <p className="page-subtitle">
            Create, update and delete subscription plans
          </p>
        </div>

        <button className="action-btn activate" onClick={openCreate}>
          + Add Plan
        </button>
      </div>

      {loading ? (
        <p>Loading subscriptions…</p>
      ) : (
        <div className="table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Description</th>
                <th>USD</th>
                <th>ILS</th>
                <th>Credits</th>
                <th>Prints</th>
                <th>Per Print</th>
                <th>Calls</th>
                <th style={{ width: "180px" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {subscriptions.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.title}</strong></td>
                  <td>{s.description || "—"}</td>
                  <td>${Number(s.price || 0).toFixed(2)}</td>
                  <td>₪{Number(s.price_ils || 0).toFixed(2)}</td>
                  <td>{s.no_credits?.toLocaleString() || "—"}</td>
                  <td>{s.total_free_prints?.toLocaleString() || "—"}</td>
                  <td>{s.per_print_limit || "—"}</td>
                  <td>{s.total_free_call || "—"}</td>
                  <td>
                    <button className="action-btn activate" onClick={() => edit(s)}>
                      Edit
                    </button>

                    <button className="action-btn deactivate" onClick={() => remove(s.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {subscriptions.length === 0 && (
                <tr>
                  <td colSpan="9" className="empty">
                    No subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-header">
              <h2>{editingId ? "Update Plan" : "Create New Plan"}</h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <form className="subscription-form modal-form" onSubmit={submit}>
              <div className="form-grid">
                <input name="title" placeholder="Plan title" value={form.title} onChange={handleChange} required />

                <input name="price" type="number" step="0.01" placeholder="Price USD" value={form.price} onChange={handleChange} required />

                <input name="price_ils" type="number" step="0.01" placeholder="Price ILS" value={form.price_ils} onChange={handleChange} required />

                <input name="duration_days" type="number" placeholder="Duration days" value={form.duration_days} onChange={handleChange} required />

                <input name="no_credits" type="number" placeholder="Credits" value={form.no_credits} onChange={handleChange} required />

                <input name="total_free_prints" type="number" placeholder="Total free prints" value={form.total_free_prints} onChange={handleChange} required />

                <input name="per_print_limit" type="number" placeholder="Per print limit" value={form.per_print_limit} onChange={handleChange} required />

                <input name="total_free_call" type="number" placeholder="Free calls" value={form.total_free_call} onChange={handleChange} required />

                <input name="time_limit_per_call" type="number" placeholder="Call limit seconds" value={form.time_limit_per_call} onChange={handleChange} required />
              </div>

              <textarea
                name="description"
                placeholder="Description"
                value={form.description}
                onChange={handleChange}
                required
              />

              <div className="form-actions">
                <button className="action-btn activate" type="submit" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Update Plan" : "Create Plan"}
                </button>

                <button className="action-btn deactivate" type="button" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}